/**
 * FE16 (audit 2026-09-25) — run one request per id and report every outcome.
 *
 * Bulk actions whose API is per-id (Recycle Bin restore / permanent delete)
 * used to loop `mutation.mutate(id, { onSuccess, onError })` over one
 * useMutation. TanStack v5 fires mutate()-level callbacks only for the LATEST
 * call, so a batch of N reported on at most one of them. This awaits every
 * request instead, a few at a time, and returns what happened to each.
 */
export interface PerIdOutcome {
  succeeded: string[]
  failed: { id: string; error: unknown }[]
}

export async function runPerId(
  ids: string[],
  run: (id: string) => Promise<unknown>,
  concurrency = 5,
): Promise<PerIdOutcome> {
  const outcome: PerIdOutcome = { succeeded: [], failed: [] }
  const queue = [...ids]
  const worker = async () => {
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      try {
        await run(id)
        outcome.succeeded.push(id)
      } catch (error) {
        outcome.failed.push({ id, error })
      }
    }
  }
  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(concurrency, ids.length)) }, worker),
  )
  return outcome
}
